import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  CircularProgress,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import ArrowBack from '@mui/icons-material/ArrowBack';
import CheckCircleOutlineOutlined from '@mui/icons-material/CheckCircleOutlineOutlined';
import ErrorOutlineOutlined from '@mui/icons-material/ErrorOutlineOutlined';
import { api } from '../api';
import PayDialog, { PAY_METHODS } from '../PayDialog';
import { date, money, Tag, type Props, type StatusTone } from '../ui';
import { statementStep } from './Statements';

const toneColor: Record<StatusTone, string> = {
  success: '#1d7348',
  warning: '#9a5b06',
  info: '#24343a',
  neutral: '#5c6c72',
};
const toneBg: Record<StatusTone, string> = {
  success: '#eef8f2',
  warning: '#fff7ea',
  info: '#f4f6f7',
  neutral: '#f4f6f7',
};

export function StatementDetail(p: Props) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [s, setS] = useState<any>(null);
  const [paying, setPaying] = useState(false);
  useEffect(() => {
    let live = true;
    setS(null);
    api(`/statements/${id}`)
      .then((d) => live && setS(d))
      .catch((e) => p.setError(e.message));
    return () => {
      live = false;
    };
  }, [id, p.version, p.workspace]);
  if (!s)
    return (
      <Box sx={{ p: 8, textAlign: 'center' }}>
        <CircularProgress />
      </Box>
    );

  const selling = s.seller_id === p.workspace;
  const peer = selling ? s.buyer_name : s.seller_name;
  const issuedByMe = (s.issued_by_seller ? s.seller_id : s.buyer_id) === p.workspace;
  const total = Number(s.total);
  const paid = s.payments
    .filter((x: any) => x.status === 'CONFIRMED')
    .reduce((sum: number, x: any) => sum + Number(x.amount), 0);
  const pending = s.payments.filter((x: any) => x.status === 'CLAIMED');
  const claimed = pending.reduce((sum: number, x: any) => sum + Number(x.amount), 0);
  const left = Math.max(0, total - paid);
  const payable = Math.max(0, left - claimed);
  const step = statementStep({ ...s, paid, claimed }, p.workspace);
  const post = (suffix: string, body: any = {}) => api(`/statements/${id}/${suffix}`, body);

  const confirm = () =>
    p.open({
      title: '确认对账单',
      fields: [],
      form: {},
      submit: () => post('confirm'),
      description: `确认 ${s.lines.length} 张订单共 ${money(total)}。确认后各订单的对账金额一并锁定，不再受理退货。`,
    });
  const reject = () =>
    p.open({
      title: '退回对账单',
      fields: [{ name: 'reason', label: '退回原因', wide: true }],
      form: { reason: '' },
      submit: (f: any) => post('reject', f),
      description: '写明哪张订单金额不符或漏单，对方修改后会重新发出。退回后这些订单可以重新对账。',
    });
  const withdraw = () =>
    p.open({
      title: '撤回对账单',
      fields: [],
      form: {},
      submit: () => post('withdraw'),
      description: '撤回后这些订单回到待对账，可以修改后重新生成。',
    });
  const record = () =>
    p.open({
      title: '登记收款',
      fields: [
        { name: 'amount', label: '金额（元）', decimals: 2 },
        {
          name: 'method',
          label: '收款方式',
          options: Object.entries(PAY_METHODS).map(([value, label]) => ({ value, label })),
        },
        { name: 'reference', label: '凭据号 / 说明', wide: true },
      ],
      form: { amount: payable.toFixed(2), method: 'BANK', reference: '' },
      submit: (f: any, key: string) => post('payments', { ...f, key }),
      description: `尚未结清 ${money(payable)}。登记后视为已确认收款，按订单先后分摊，买方账上同步记为已付。`,
    });
  const confirmPayment = (x: any) =>
    p.open({
      title: '确认收款',
      fields: [],
      form: {},
      submit: () => post(`payment-claims/${x.id}`, { action: 'CONFIRM' }),
      description: `请先核对本企业账户已到账 ${money(x.amount)}（${PAY_METHODS[x.method]}${x.reference ? `，${x.reference}` : ''}）。确认后按订单先后分摊，双方账上同时记为已收 / 已付。`,
    });
  const rejectPayment = (x: any) =>
    p.open({
      title: '未收到这笔付款',
      fields: [{ name: 'reason', label: '原因', wide: true }],
      form: { reason: '' },
      submit: (f: any) => post(`payment-claims/${x.id}`, { action: 'REJECT', reason: f.reason }),
      description: `买方声明已付 ${money(x.amount)}（${PAY_METHODS[x.method]}）。请写明未到账或金额不符的情况，买方核对后可重新提交。`,
    });

  // ---- The one thing to do next ------------------------------------------------------------
  type Next = {
    tone: StatusTone;
    title: string;
    detail: string;
    primary?: [string, () => void];
    secondary?: [string, () => void];
  };
  let next: Next;
  if (s.status === 'PENDING')
    next = issuedByMe
      ? {
          tone: 'info',
          title: `等待${peer}确认对账单`,
          detail: '对方确认后各订单金额锁定；需要修改可先撤回。',
          secondary: ['撤回', withdraw],
        }
      : {
          tone: 'warning',
          title: `请核对 ${s.lines.length} 张订单，共 ${money(total)}`,
          detail: '逐单核对金额，无误后确认；有出入可退回并写明原因。',
          primary: ['确认对账单', confirm],
          secondary: ['退回', reject],
        };
  else if (s.status === 'REJECTED')
    next = {
      tone: 'neutral',
      title: issuedByMe ? `${peer}退回了对账单` : '已退回',
      detail: `原因：${s.reject_reason}。这些订单已回到待对账，可修改后重新生成。`,
    };
  else if (s.status === 'WITHDRAWN')
    next = {
      tone: 'neutral',
      title: '对账单已撤回',
      detail: '这些订单已回到待对账。',
    };
  else if (left <= 0)
    next = {
      tone: 'success',
      title: '对账单已结清',
      detail: `共 ${money(total)}，已按订单分摊入账。`,
    };
  else if (selling)
    next = pending.length
      ? {
          tone: 'warning',
          title: `${peer}已付款 ${money(pending[0].amount)}，请核对到账`,
          detail: `${PAY_METHODS[pending[0].method]}${pending[0].reference ? ` · ${pending[0].reference}` : ''}。确认后按订单先后分摊。`,
          primary: ['确认收款', () => confirmPayment(pending[0])],
          secondary: ['未收到', () => rejectPayment(pending[0])],
        }
      : {
          tone: 'info',
          title: `等待${peer}付款 ${money(left)}`,
          detail: '对方付款后会提交给你确认；线下已收到也可直接登记。',
          secondary: ['登记收款', record],
        };
  else
    next =
      payable > 0
        ? {
            tone: 'warning',
            title: `向${peer}付款 ${money(payable)}`,
            detail: claimed
              ? `另有 ${money(claimed)} 已提交，等待对方确认收款。`
              : '一次付清整张对账单，付款后提交由对方确认。',
            primary: ['去付款', () => setPaying(true)],
          }
        : {
            tone: 'info',
            title: `已提交付款 ${money(claimed)}，等待${peer}确认收款`,
            detail: '对方核对到账后，对账单即结清。',
          };
  const NextIcon =
    next.tone === 'success'
      ? CheckCircleOutlineOutlined
      : next.tone === 'warning'
        ? ErrorOutlineOutlined
        : null;

  return (
    <>
      <Button
        component={Link}
        to="/statements"
        size="small"
        startIcon={<ArrowBack />}
        sx={{ mb: 2 }}
      >
        返回对账结算
      </Button>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <Box sx={{ flex: 1, minWidth: 260 }}>
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 1 }}>
              <Tag tone="info">{selling ? '应收对账单' : '应付对账单'}</Tag>
              <Tag tone={step.tone}>{step.label}</Tag>
            </Stack>
            <Typography sx={{ fontSize: 24, fontWeight: 700, lineHeight: 1.3 }}>{peer}</Typography>
            <Typography color="text.secondary" sx={{ fontSize: 13, mt: 0.5 }}>
              {s.number} · {s.period.replace('-', ' 年 ')} 月 · {s.lines.length} 张订单 ·{' '}
              {issuedByMe ? '本企业' : peer}
              {s.created_name ? ` ${s.created_name}` : ''} 于 {date(s.created_at)} 发出
              {s.note && ` · ${s.note}`}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 5, alignItems: 'flex-end' }}>
            <Box>
              <Typography color="text.secondary" sx={{ fontSize: 12 }}>
                对账金额
              </Typography>
              <Typography sx={{ fontSize: 24, fontWeight: 700, lineHeight: 1.3 }}>
                {money(total)}
              </Typography>
            </Box>
            {s.status === 'CONFIRMED' && (
              <Box>
                <Typography color="text.secondary" sx={{ fontSize: 12 }}>
                  已{selling ? '收' : '付'}
                </Typography>
                <Typography sx={{ fontSize: 24, fontWeight: 700, lineHeight: 1.3 }}>
                  {money(paid)}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>

        <Box
          sx={{
            mt: 2.5,
            px: 2,
            py: next.primary ? 1.5 : 1.25,
            borderRadius: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 1.25,
            flexWrap: 'wrap',
            bgcolor: toneBg[next.tone],
          }}
        >
          {NextIcon && <NextIcon sx={{ color: toneColor[next.tone], fontSize: 18 }} />}
          <Box sx={{ flex: 1, minWidth: 220 }}>
            <Typography
              component="span"
              sx={{ fontSize: 14, fontWeight: 650, color: toneColor[next.tone] }}
            >
              {next.title}
            </Typography>
            <Typography component="span" color="text.secondary" sx={{ fontSize: 12.5, ml: 1.5 }}>
              {next.detail}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            {next.secondary && (
              <Button
                variant="outlined"
                color="inherit"
                onClick={next.secondary[1]}
                sx={{ borderColor: '#cfd9dc', color: 'text.primary', bgcolor: '#fff' }}
              >
                {next.secondary[0]}
              </Button>
            )}
            {next.primary && (
              <Button variant="contained" disableElevation onClick={next.primary[1]}>
                {next.primary[0]}
              </Button>
            )}
          </Stack>
        </Box>
      </Paper>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1fr) 340px' },
          gap: 3,
          alignItems: 'start',
        }}
      >
        <Paper sx={{ minWidth: 0 }}>
          <div className="panel-title">
            <Typography sx={{ fontSize: 15, fontWeight: 650 }}>
              订单明细 · {s.lines.length} 张
            </Typography>
          </div>
          <Box sx={{ overflowX: 'auto' }}>
            <Table>
              <TableHead>
                <TableRow sx={{ '& th': { whiteSpace: 'nowrap' } }}>
                  <TableCell sx={{ minWidth: 200 }}>订单</TableCell>
                  <TableCell>完成收货</TableCell>
                  <TableCell align="right">订单金额</TableCell>
                  <TableCell align="right">对账金额</TableCell>
                  {s.status === 'CONFIRMED' && (
                    <TableCell align="right">已{selling ? '收' : '付'}</TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {s.lines.map((l: any) => (
                  <TableRow
                    key={l.trade_id}
                    hover
                    // The whole row opens the order; the name is the visible link.
                    onClick={() => navigate(`/orders/${l.trade_id}`)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell>
                      <Typography
                        component={Link}
                        to={`/orders/${l.trade_id}`}
                        sx={{
                          fontSize: 14,
                          fontWeight: 600,
                          color: 'primary.main',
                          textDecoration: 'none',
                          'tr:hover &': { textDecoration: 'underline' },
                        }}
                      >
                        {l.sku_name}
                        {l.line_count > 1 && ` 等 ${l.line_count} 种`}
                      </Typography>
                      <Typography color="text.secondary" sx={{ fontSize: 12 }}>
                        {l.number}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{date(l.delivered_at)}</TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap', color: 'text.secondary' }}>
                      {money(l.order_amount)}
                    </TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap', fontWeight: 600 }}>
                      {money(l.amount)}
                    </TableCell>
                    {s.status === 'CONFIRMED' && (
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                        {money(l.paid)}
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
              <TableFooter>
                <TableRow>
                  <TableCell colSpan={3} sx={{ fontSize: 13, color: 'text.secondary' }}>
                    合计
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      fontSize: 15,
                      fontWeight: 700,
                      color: 'text.primary',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {money(total)}
                  </TableCell>
                  {s.status === 'CONFIRMED' && (
                    <TableCell
                      align="right"
                      sx={{ fontSize: 13, color: 'text.primary', whiteSpace: 'nowrap' }}
                    >
                      {money(paid)}
                    </TableCell>
                  )}
                </TableRow>
              </TableFooter>
            </Table>
          </Box>
        </Paper>

        <Paper>
          <div className="panel-title">
            <Typography sx={{ fontSize: 15, fontWeight: 650 }}>收付款</Typography>
          </div>
          <Box sx={{ p: 3 }}>
            {s.status !== 'CONFIRMED' ? (
              <Typography color="text.secondary" sx={{ fontSize: 13 }}>
                双方确认对账单后即可付款。
              </Typography>
            ) : (
              <>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
                  <Typography sx={{ fontSize: 12.5 }}>
                    已{selling ? '收' : '付'} {money(paid)}
                  </Typography>
                  <Typography color="text.secondary" sx={{ fontSize: 12.5 }}>
                    余 {money(left)}
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={Math.min(100, (paid / Math.max(total, 0.01)) * 100)}
                  color="success"
                  sx={{ height: 6, borderRadius: 3, bgcolor: '#edf1f2' }}
                />
                {s.payments.length > 0 && (
                  <Stack spacing={1.25} sx={{ mt: 2 }}>
                    {s.payments.map((x: any) => (
                      <Box key={x.id} sx={{ p: 1.5, borderRadius: 1, bgcolor: '#f6f8f8' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography sx={{ fontSize: 13.5, fontWeight: 650, flex: 1 }}>
                            {money(x.amount)} · {PAY_METHODS[x.method]}
                          </Typography>
                          <Tag
                            tone={
                              x.status === 'CONFIRMED'
                                ? 'success'
                                : x.status === 'REJECTED'
                                  ? 'neutral'
                                  : 'warning'
                            }
                          >
                            {x.status === 'CONFIRMED'
                              ? '已确认收款'
                              : x.status === 'REJECTED'
                                ? '未收到'
                                : '待确认收款'}
                          </Tag>
                        </Box>
                        <Typography color="text.secondary" sx={{ fontSize: 12, mt: 0.5 }}>
                          {date(x.created_at)}
                          {x.reference && ` · ${x.reference}`}
                          {x.status === 'REJECTED' && x.reject_reason && ` · ${x.reject_reason}`}
                        </Typography>
                        {selling && x.status === 'CLAIMED' && (
                          <Box
                            sx={{
                              display: 'grid',
                              gridTemplateColumns: '1fr 1fr',
                              gap: 1,
                              mt: 1.5,
                            }}
                          >
                            <Button
                              size="small"
                              variant="outlined"
                              color="inherit"
                              onClick={() => rejectPayment(x)}
                              sx={{
                                borderColor: '#cfd9dc',
                                color: 'text.primary',
                                bgcolor: '#fff',
                              }}
                            >
                              未收到
                            </Button>
                            <Button
                              size="small"
                              variant="contained"
                              disableElevation
                              onClick={() => confirmPayment(x)}
                            >
                              确认收款
                            </Button>
                          </Box>
                        )}
                      </Box>
                    ))}
                  </Stack>
                )}
              </>
            )}
            <Typography color="text.secondary" sx={{ fontSize: 11.5, mt: 2 }}>
              款项在双方之间线下支付，平台只记录，不经手资金。确认的付款按订单先后分摊，每张订单都会显示已付金额。
            </Typography>
          </Box>
        </Paper>
      </Box>

      {paying && (
        <PayDialog
          path={`/statements/${s.id}/payment-claims`}
          peer={peer}
          left={payable}
          payee={s.payee}
          onClose={() => setPaying(false)}
          onDone={() => {
            setPaying(false);
            p.reload();
            p.notify('已提交付款，等待对方确认收款');
          }}
        />
      )}
    </>
  );
}
